import { Box, Center } from "@chakra-ui/layout"
import React, { PropsWithChildren, ReactElement, useCallback, useMemo, useState } from "react"
import styled, { css } from "styled-components"

import Icon from "~/components/atoms/Icon"
import SelectModal from "~/components/molecules/SelectModal"
import { Color, RadiusSize, SpacingSize, TextSize } from "~/consts/style-mixins"
import { Option } from "~/services/createOptions"

const DEFAULT_MAIN_ACTION_LABEL = "確定する"
const DEFAULT_SUB_ACTION_LABEL = "キャンセル"

type Judge<IsAutoNumberMode> = IsAutoNumberMode extends true ? number : string

type Props<IsAutoNumberMode extends boolean, StringOrNumber = Judge<IsAutoNumberMode>> = PropsWithChildren<{
  /** isAutoNumberModeを指定(`<SelectboxMultiple isAutoNumberMode />`)するとselectedやonChangeで扱う型が`number[]`になる */
  isAutoNumberMode?: IsAutoNumberMode
  label?: string
  selected: StringOrNumber[]
  options: Option[]
  onChange: (next: StringOrNumber[]) => void
  disabled?: boolean
  error?: boolean
  mainAction?: {
    label: string
  }
  subAction?: {
    label: string
  }
  // 選択した際に、他の選択肢をdisabledにするオプション。例）"該当なし" など。
  exclusiveOptionValue?: string
}>

export type SelectboxMultipleProps<IsAutoNumberMode extends boolean = false> = Props<IsAutoNumberMode>

export default function SelectboxMultiple<IsAutoNumberMode extends boolean = false>(
  props: Props<IsAutoNumberMode>
): ReactElement

export default function SelectboxMultiple(props: Props<any>) {
  const {
    isAutoNumberMode,
    label,
    options,
    selected,
    onChange,
    disabled,
    error,
    mainAction,
    subAction,
    exclusiveOptionValue,
  } = props
  const [currentSelected, setCurrentSelected] = useState(selected)
  const [isOpenModal, setOpenModal] = useState(false)

  const inputText = useMemo(() => {
    if (selected.length === 0) {
      return "選択してください"
    }
    return (
      options
        // NOTE: stringとnumber両方に対応するために曖昧比較演算子にしてる
        .filter((option) => selected.find((s) => s == option.value))
        .map((option) => option.text)
        .join(" / ")
    )
  }, [selected, options])

  const checkboxOptions = useMemo(() => {
    if (exclusiveOptionValue && currentSelected.map(String).includes(exclusiveOptionValue)) {
      return options.map((o) => ({ ...o, disabled: o.value != exclusiveOptionValue }))
    } else if (exclusiveOptionValue && currentSelected.length > 0) {
      return options.map((o) => ({ ...o, disabled: o.value == exclusiveOptionValue }))
    } else {
      return options.map((o) => ({ ...o, disabled: false }))
    }
  }, [currentSelected])

  const openModal = useCallback(() => {
    if (disabled) return
    setCurrentSelected(selected)
    setOpenModal(true)
  }, [selected, disabled])

  const closeModal = useCallback(() => {
    setOpenModal(false)
  }, [])

  const onClickMainAction = useCallback(() => {
    let target = currentSelected
    // isAutoNumberModeがtrueの時、currentSelectedをstringからnumberに変換する
    if (isAutoNumberMode) {
      target = target.map(Number)
    }
    onChange(target)
    setOpenModal(false)
  }, [currentSelected])

  return (
    <>
      <Box position="relative">
        <StyledInputLike onClick={openModal} disabled={disabled} error={error}>
          {inputText}
        </StyledInputLike>
        <Center h="100%" position="absolute" top={0} right={16}>
          <Icon type="Open" size={16} color={Color.Gray} />
        </Center>
      </Box>
      {isOpenModal && (
        <SelectModal
          labelName={label}
          checkbox={{
            values: currentSelected,
            items: checkboxOptions,
            onClick: (next) => setCurrentSelected(next),
          }}
          mainAction={{
            label: mainAction?.label ?? DEFAULT_MAIN_ACTION_LABEL,
            action: onClickMainAction,
          }}
          subAction={{
            label: subAction?.label ?? DEFAULT_SUB_ACTION_LABEL,
            action: closeModal,
          }}
        />
      )}
    </>
  )
}

interface ISelectboxMultiplePropsStyledInput {
  disabled?: boolean
  error?: boolean
}

// NOTE: FormSelectboxForOccupationsが依存している
export const StyledInputLike = styled.div`
  caret-color: transparent;
  overflow: hidden;
  background: ${Color.White};
  border: 1px solid ${Color.Gray};
  box-sizing: border-box;
  border-radius: ${RadiusSize.Small}px;
  font-size: ${TextSize.M}px;
  line-height: 100%;
  color: ${Color.Black};
  outline: none;
  cursor: pointer;
  transition: 0.2s;
  padding: ${SpacingSize.M}px;
  padding-right: ${SpacingSize._3XL}px;
  /* NOTE: 以下のようにwidthをcalcで算出しないと、width 100%とwhite-space nowrapとflexboxがおかしくなる(chromeでもsafariでも) */
  width: calc(100vw - 64px);
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover {
    border: 1px solid ${Color.Gray};
  }
  &:focus {
    border: 1px solid ${Color.Primary};
  }
  ${({ disabled }: ISelectboxMultiplePropsStyledInput) =>
    disabled &&
    css`
      background: ${Color.BorderGray};
      border-color: ${Color.LightGray};
    `}

  ${({ error }: ISelectboxMultiplePropsStyledInput) =>
    error &&
    css`
      background: ${Color.AttentionLight};
      border-color: ${Color.Attention};
    `}
`
